import { useQuery } from '@apollo/client';
import { InputMask } from '@react-input/mask';
import { Balance } from 'components/Balance/Balance';
import { Button } from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { FormError } from 'components/FormError/FormError';
import { Input } from 'components/Input/Input';
import { Label } from 'components/Label/Label';
import { MembershipPaymentWarning } from 'components/MembershipPaymentWarning';
import { Modal } from 'components/Modal/Modal';
import { Portal } from 'components/Portal/Portal';
import { Select } from 'components/Select/Select';
import { Textarea } from 'components/Textarea/Textarea';
import dayjs from 'dayjs';
import { validateDate } from 'lib/utils';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { USER_MEMBERSHIP_PAYMENT_WARNING } from 'services/users/query';

type AddConsumptionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (data: any) => void;
    isLoading?: boolean;
    editConsumption?: any;
    member: any;
};

export const AddConsumptionModal: React.FC<AddConsumptionModalProps> = ({
    isOpen,
    onClose,
    onAdd,
    isLoading = false,
    editConsumption,
    member,
}) => {
    const { t } = useTranslation('forms');
    const { t: p } = useTranslation('documents');

    const TYPES = [
        { value: 'CONSUMPTION', label: `${p('CONSUMPTION_TRANSACTIONS.CONSUMPTION')} (-)` },
        { value: 'WINE', label: `${p('CONSUMPTION_TRANSACTIONS.WINE')} (-)` },
        { value: 'CATERING', label: `${p('CONSUMPTION_TRANSACTIONS.CATERING')} (-)` },
        { value: 'DEBIT', label: `${p('CONSUMPTION_TRANSACTIONS.DEBIT')} (-)` },
        { value: 'DEPOSIT', label: `${p('CONSUMPTION_TRANSACTIONS.DEPOSIT')} (+)` },
        { value: 'CREDIT', label: `${p('CONSUMPTION_TRANSACTIONS.CREDIT')} (+)` },
        { value: 'REMAINDER_BALANCE_CZR', label: `${p('CONSUMPTION_TRANSACTIONS.REMAINDER_BALANCE_CZR')} (-)` },
        { value: 'REMAINDER_BALANCE', label: `${p('CONSUMPTION_TRANSACTIONS.REMAINDER_BALANCE')} (-)` },
        // { value: 'REFUND', label: `${p('CONSUMPTION_TRANSACTIONS.REFUND')} (-)`  },
        // { value: 'SUSPENSION_FEE', label: `${p('CONSUMPTION_TRANSACTIONS.REFUND')} (-)` },
    ];

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        reset,
        watch,
    } = useForm();

    const { data: warningData } = useQuery(USER_MEMBERSHIP_PAYMENT_WARNING, {
        variables: { id: member?.id },
        skip: !isOpen || !member?.id || !!editConsumption,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        setValue('date', dayjs().format('DD.MM.YYYY'));
    }, [setValue]);

    useEffect(() => {
        if (editConsumption) {
            setValue('date', dayjs(editConsumption.date).format('DD.MM.YYYY'));
            setValue('invoiceNumber', editConsumption.invoiceNumber);
            setValue('type', editConsumption.type);
            setValue('amount', editConsumption.amount);
            setValue('note', editConsumption.note);
            setValue('affectedBalance', editConsumption.affectedBalance === 'REMAINDER');
        }
    }, [editConsumption, setValue]);

    const onSubmit = (data: any) => {
        data.affectedBalance = data.affectedBalance ? 'REMAINDER' : 'REGULAR';
        onAdd(data);
    };

    const closeModal = () => {
        onClose();
        reset();
    };

    return (
        <Portal>
            <Modal isOpen={isOpen} onClose={closeModal} maxWidth={512} className="flex flex-col gap-4">
                <div>
                    <div className="text-xl font-semibold text-zinc-700 mb-2">{p('addConsumptionTitle')}</div>
                    <div className="text-xs text-zinc-600">
                        {p('currentBalanceSubtitle')} <Balance balance={member.balance} />
                    </div>
                    <div className="text-xs text-zinc-600">
                        {p('remainderBalanceSubtitle')}{' '}
                        <Balance balance={{ amount: member.remainderBalance, currency: 'CHF' }} />
                    </div>
                </div>
                <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        <Label>{t('date')}</Label>
                        <InputMask
                            component={Input}
                            placeholder={t('datePlaceholder')}
                            mask="__.__.____"
                            replacement={{ _: /\d/ }}
                            error={!!errors.date}
                            {...register('date', {
                                validate: value => validateDate(value) || t('invalidDateError'),
                                required: t('dateRequired'),
                            })}
                        />
                        <FormError error={errors.date} />
                    </div>
                    <div>
                        <Label>{t('receiptLabel')}</Label>
                        <Input {...register('invoiceNumber')} placeholder={t('receiptPlaceholder')} />
                    </div>
                    <div>
                        <Label>{t('type')}</Label>
                        <Controller
                            control={control}
                            name="type"
                            rules={{ required: t('typeRequired') }}
                            render={({ field: { onChange, value } }) => (
                                <Select
                                    options={TYPES}
                                    onChange={(val: any) => onChange(val.value)}
                                    value={TYPES.find(s => s.value === value)}
                                    error={!!errors.type}
                                />
                            )}
                        />
                        <FormError error={errors.type} />
                    </div>
                    <div>
                        <Label>{t('amountLabel')}</Label>
                        <Input
                            {...register('amount', {
                                required: t('amountRequired'),
                            })}
                            type="number"
                            step="0.01"
                            placeholder={t('amountLabel')}
                            error={!!errors.amount}
                        />
                        <FormError error={errors.amount} />
                    </div>
                    <MembershipPaymentWarning
                        type={watch('type')}
                        amount={watch('amount')}
                        openInvoice={warningData?.user?.openMembershipInvoice}
                        renewalFeeGross={warningData?.user?.renewalFeeGross}
                        isEdit={!!editConsumption}
                    />
                    <div>
                        <Label>{t('noteLabel')}</Label>
                        <Textarea {...register('note')} placeholder={t('noteLabel')} />
                    </div>
                    <div>
                        <Controller
                            control={control}
                            name="affectedBalance"
                            defaultValue={false}
                            render={({ field: { onChange, value } }) => {
                                const currentType = watch('type');
                                const isRemainderType = currentType === 'REMAINDER_BALANCE_CZR';
                                return (
                                    <Checkbox 
                                        onChange={onChange} 
                                        checked={isRemainderType || value}
                                        disabled={isRemainderType}
                                    >
                                        {p('deductFromRemainderButton')}
                                    </Checkbox>
                                );
                            }}
                        />
                    </div>

                    <div className="flex justify-end gap-4">
                        <Button type="button" variant="outline" onClick={closeModal} disabled={isLoading}>
                            {p('cancelModalButton')}
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {p('saveButton')}
                        </Button>
                    </div>
                </form>
            </Modal>
        </Portal>
    );
};
